'use strict';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Card, CardActionArea, CardContent, Checkbox, CircularProgress, Container, FormControlLabel, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { Apartment, ArrowBack, ArrowForwardRounded, Lock, Login, Person, PointOfSaleRounded, SearchRounded } from '@mui/icons-material';
import api from '../../api/api';
import { commitAuthSession } from '../../auth/authStorage';
import logo from '../assets/logo.png';
import backgroundImage from '../assets/integranube-background.jpg';

const SCRIPT_ID = 'integranube-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';


function normalizeLoginIdentifier(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  const rutCompact = upper
    .replace(/\./g, '')
    .replace(/-/g, '')
    .replace(/\s+/g, '')
    .replace(/[^0-9K]/g, '');

  const looksLikeRut =
    !raw.includes('@') && /^[0-9]{7,8}[0-9K]$/.test(rutCompact);

  if (looksLikeRut) {
    const body = rutCompact.slice(0, -1).replace(/^0+/, '') || '0';
    const dv = rutCompact.slice(-1);
    return `${body}-${dv}`;
  }

  return raw.toLowerCase();
}

function tenantId(tenant) {
  return String(
    tenant?.tenantid ||
      tenant?.tenantId ||
      tenant?.tenant_id ||
      tenant?.id_tenant ||
      tenant?.id ||
      ''
  ).trim();
}

function tenantName(tenant) {
  return String(
    tenant?.name ||
      tenant?.nombre ||
      tenant?.nombre_fantasia ||
      tenant?.razon_social ||
      tenant?.tenant_name ||
      tenant?.empresa ||
      'Empresa'
  );
}

function initials(tenant) {
  const words = tenantName(tenant)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return 'IN';

  const first = words[0]?.[0] || '';
  const second = words.length > 1 ? words[1]?.[0] : words[0]?.[1] || '';
  return `${first}${second}`.toUpperCase();
}

function persistLocalAuthSession({ token, tenant, user }) {
  const id = tenantId(tenant);

  if (!token || !id) {
    throw new Error('AUTH_SESSION_INCOMPLETE');
  }

  const normalizedTenant = {
    ...(tenant || {}),
    tenantid: id,
  };

  localStorage.setItem('token', token);
  localStorage.setItem('tenantid', id);
  localStorage.setItem('tenant', JSON.stringify(normalizedTenant));
  localStorage.setItem('user', JSON.stringify(user || null));
}

function clearLocalAuthSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('tenantid');
  localStorage.removeItem('tenantId');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('tenant');
  localStorage.removeItem('user');
}

let turnstileScriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('TURNSTILE_BROWSER_REQUIRED'));
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    let settled = false;

    const finishOk = () => {
      if (settled) return;

      if (!window.turnstile) {
        finishError(new Error('TURNSTILE_API_NOT_AVAILABLE'));
        return;
      }

      settled = true;
      resolve(window.turnstile);
    };

    const finishError = (error) => {
      if (settled) return;
      settled = true;
      turnstileScriptPromise = null;
      reject(error instanceof Error ? error : new Error('TURNSTILE_SCRIPT_LOAD_FAILED'));
    };

    let script = document.getElementById(SCRIPT_ID);

    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    script.addEventListener('load', finishOk, { once: true });
    script.addEventListener(
      'error',
      () => finishError(new Error('TURNSTILE_SCRIPT_LOAD_FAILED')),
      { once: true }
    );

    window.setTimeout(() => {
      if (window.turnstile) finishOk();
    }, 0);
  });

  return turnstileScriptPromise;
}

const Turnstile = forwardRef(function Turnstile(
  { siteKey, onToken, onExpired, onError, onReady },
  controlRef
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const callbacksRef = useRef({
    onToken,
    onExpired,
    onError,
    onReady,
  });

  useEffect(() => {
    callbacksRef.current = {
      onToken,
      onExpired,
      onError,
      onReady,
    };
  }, [onToken, onExpired, onError, onReady]);

  useImperativeHandle(
    controlRef,
    () => ({
      reset() {
        try {
          if (widgetIdRef.current != null && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
          }
        } catch (_) {
        }
      },

      getValue() {
        try {
          if (widgetIdRef.current == null || !window.turnstile) return '';
          return String(window.turnstile.getResponse(widgetIdRef.current) || '').trim();
        } catch (_) {
          return '';
        }
      },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const mountWidget = async () => {
      try {
        const turnstile = await loadTurnstileScript();

        if (cancelled || !containerRef.current) return;

        if (widgetIdRef.current != null) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch (_) {
          }
          widgetIdRef.current = null;
        }

        const widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'login',
          theme: 'light',
          size: 'normal',
          callback: (value) => {
            callbacksRef.current.onToken?.(String(value || '').trim());
          },
          'expired-callback': () => {
            callbacksRef.current.onToken?.('');
            callbacksRef.current.onExpired?.();
          },
          'timeout-callback': () => {
            callbacksRef.current.onToken?.('');
            callbacksRef.current.onExpired?.();
          },
          'error-callback': (code) => {
            callbacksRef.current.onToken?.('');
            callbacksRef.current.onError?.(String(code || 'unknown'));
          },
        });

        widgetIdRef.current = widgetId;
        callbacksRef.current.onReady?.();
      } catch (error) {
        if (!cancelled) {
          callbacksRef.current.onToken?.('');
          callbacksRef.current.onError?.(
            error?.message || 'TURNSTILE_SCRIPT_LOAD_FAILED'
          );
        }
      }
    };

    mountWidget();

    return () => {
      cancelled = true;

      try {
        if (widgetIdRef.current != null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_) {
      } finally {
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return <Box ref={containerRef} sx={{ minHeight: 65 }} />;
});


export default function LoginForm({onLogin}){
  const siteKey=process.env.REACT_APP_TURNSTILE_SITE_KEY||'';
  const [step,setStep]=useState('login'); const [loading,setLoading]=useState(false); const [error,setError]=useState('');
  const [identifier,setIdentifier]=useState(()=>localStorage.getItem('rememberedLogin')||''); const [password,setPassword]=useState(''); const [remember,setRemember]=useState(()=>Boolean(localStorage.getItem('rememberedLogin')));
  const [token,setToken]=useState(''); const [user,setUser]=useState(null); const [tenants,setTenants]=useState([]); const [search,setSearch]=useState(''); const [turnstileToken,setTurnstileToken]=useState('');
  const turnstileRef = useRef(null);

  const resetTurnstile = () => {
    setTurnstileToken('');
    try {
      turnstileRef.current?.reset?.();
    } catch (_) {
    }
  };
  const options=useMemo(()=>tenants.filter(t=>tenantId(t)).filter(t=>{const q=search.trim().toLowerCase();return !q||JSON.stringify(t).toLowerCase().includes(q);}),[tenants,search]);

  const submit=async(e)=>{e.preventDefault(); const loginId=normalizeLoginIdentifier(identifier); if(!loginId||!password){setError('Ingresa correo o RUT y contraseña.');return;} if(siteKey&&!turnstileToken){setError('Completa la verificación de seguridad.');return;}
    setLoading(true);setError(''); try{
      const {data}=await api.post('/auth/login',{email:loginId,identifier:loginId,login:loginId,password,turnstileToken:turnstileToken||undefined});
      if(!data?.token)throw new Error('TOKEN_MISSING');
      sessionStorage.setItem('token',data.token);
      const me=await api.get('/auth/me',{headers:{Authorization:`Bearer ${data.token}`}});
      const allowed=Array.isArray(me?.data?.allowedTenants)?me.data.allowedTenants:[]; const meUser=me?.data?.user||null;
      if(remember)localStorage.setItem('rememberedLogin',loginId); else localStorage.removeItem('rememberedLogin');
      setToken(data.token);setUser(meUser);setTenants(allowed);
      if(!allowed.length){resetTurnstile();setError('Tu usuario no tiene empresas asignadas.');return;}
      if(allowed.length===1){
        commitAuthSession({token:data.token,user:meUser,tenant:allowed[0]});
        persistLocalAuthSession({token:data.token,user:meUser,tenant:allowed[0]});
        await onLogin?.({token:data.token,user:meUser,tenant:allowed[0]});
        return;
      }
      setStep('tenant');
    }catch(err){sessionStorage.removeItem('token');clearLocalAuthSession();resetTurnstile();setError(err?.response?.data?.error||err?.response?.data?.message||'Credenciales incorrectas o error de servidor.');}
    finally{setLoading(false);}
  };
  const choose=async(t)=>{setLoading(true);setError('');try{commitAuthSession({token,user,tenant:t});persistLocalAuthSession({token,user,tenant:t});await onLogin?.({token,user,tenant:t});}catch(e){clearLocalAuthSession();setError('No fue posible seleccionar la empresa.');}finally{setLoading(false);}};
  const goBack=()=>{sessionStorage.clear();clearLocalAuthSession();setStep('login');setToken('');setUser(null);setTenants([]);setPassword('');setTurnstileToken('');setError('');};

  return <Box sx={{minHeight:'100vh',display:'grid',placeItems:'center',p:2,backgroundImage:`linear-gradient(rgba(7,11,19,.76),rgba(7,11,19,.9)),url(${backgroundImage})`,backgroundSize:'cover',backgroundPosition:'center'}}>
    <Container maxWidth="md"><Paper elevation={20} sx={{overflow:'hidden',borderRadius:4,display:'grid',gridTemplateColumns:{xs:'1fr',md:'320px 1fr'}}}>
      <Box sx={{display:{xs:'none',md:'flex'},p:4,color:'#fff',flexDirection:'column',justifyContent:'space-between',background:'linear-gradient(180deg,#0d2747,#07111f)'}}>
        <Box>
          <Box component="img" src={logo} alt="IntegraNube" sx={{width:'100%',maxWidth:240}}/>
          <Typography variant="h5" fontWeight={950} sx={{mt:4}}>IntegraNube POS Retail</Typography>
          <Typography sx={{mt:1,color:'rgba(255,255,255,.72)',lineHeight:1.6}}>Atención rápida, ventas simples y control de caja en un solo lugar.</Typography>
          <Stack spacing={1.15} sx={{mt:3}}>
            {['Venta rápida y búsqueda de productos','Caja, pagos y movimientos','Boletas y facturas desde el POS','Continuidad operativa para la jornada'].map((item)=><Stack key={item} direction="row" spacing={1.1} alignItems="center"><Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:'#42a5f5',flex:'0 0 auto'}}/><Typography variant="body2" sx={{color:'rgba(255,255,255,.82)'}}>{item}</Typography></Stack>)}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center"><PointOfSaleRounded/><Typography variant="caption">IntegraNube · Punto de venta</Typography></Stack>
      </Box>
      <Box sx={{p:{xs:2.5,sm:4}}}>
        <Box component="img" src={logo} alt="IntegraNube" sx={{display:{xs:'block',md:'none'},width:190,mb:3}}/>
        {step==='login'?<Box component="form" onSubmit={submit}>
          <Stack spacing={2}>
            <Box><Typography variant="overline" color="primary" fontWeight={900}>IntegraNube POS</Typography><Typography variant="h5" fontWeight={950}>Bienvenido</Typography><Typography color="text.secondary" variant="body2">Ingrese sus credenciales para comenzar a operar.</Typography></Box>
            <TextField label="Correo o RUT" value={identifier} onChange={e=>setIdentifier(e.target.value)} autoComplete="username" InputProps={{startAdornment:<InputAdornment position="start"><Person/></InputAdornment>}}/>
            <TextField label="Contraseña" type="password" value={password} onChange={e=>setPassword(e.target.value)} autoComplete="current-password" InputProps={{startAdornment:<InputAdornment position="start"><Lock/></InputAdornment>}}/>
            {siteKey?<Box sx={{display:'flex',justifyContent:'center',p:1,border:'1px solid',borderColor:'divider',borderRadius:2}}><Turnstile ref={turnstileRef} siteKey={siteKey} onToken={setTurnstileToken} onExpired={()=>setError('La verificación de seguridad expiró. Valida nuevamente.')} onError={()=>setError('No se pudo completar la verificación de seguridad. Intenta nuevamente.')}/></Box>:<Alert severity="warning">Turnstile no configurado en este entorno.</Alert>}
            <FormControlLabel control={<Checkbox checked={remember} onChange={e=>setRemember(e.target.checked)}/>} label="Recordar usuario"/>
            {error&&<Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" size="large" disabled={loading||(siteKey&&!turnstileToken)} startIcon={loading?<CircularProgress size={18} color="inherit"/>:<Login/>}>{loading?'Validando…':'Iniciar sesión'}</Button>
          </Stack>
        </Box>:<Stack spacing={2}>
          <Box><Typography variant="overline" color="primary" fontWeight={900}>Empresa</Typography><Typography variant="h5" fontWeight={950}>Seleccione su empresa</Typography><Typography color="text.secondary" variant="body2">{user?.name||user?.nombre||user?.email||'Usuario conectado'}</Typography></Box>
          {tenants.length>4&&<TextField size="small" placeholder="Buscar empresa…" value={search} onChange={e=>setSearch(e.target.value)} InputProps={{startAdornment:<InputAdornment position="start"><SearchRounded/></InputAdornment>}}/>}
          {error&&<Alert severity="error">{error}</Alert>}
          <Stack spacing={1.2} sx={{maxHeight:420,overflowY:'auto'}}>{options.map(t=><Card key={tenantId(t)} variant="outlined"><CardActionArea onClick={()=>!loading&&choose(t)}><CardContent><Stack direction="row" spacing={1.5} alignItems="center"><Avatar sx={{bgcolor:'primary.main'}}>{initials(t)}</Avatar><Box sx={{flex:1,minWidth:0}}><Typography fontWeight={900} noWrap>{tenantName(t)}</Typography><Typography variant="caption" color="text.secondary">{t?.rut||t?.rut_empresa||tenantId(t)}</Typography></Box>{loading?<CircularProgress size={20}/>:<ArrowForwardRounded/>}</Stack></CardContent></CardActionArea></Card>)}</Stack>
          <Button variant="outlined" startIcon={<ArrowBack/>} onClick={goBack} disabled={loading}>Cambiar cuenta</Button>
        </Stack>}
      </Box>
    </Paper></Container>
  </Box>;
}
